/**
 * WeekView — emotional weekly summary. Each of the 7 days is a horizontal
 * "leaf" card. The intent is to surface the *feeling* of the week
 * (score, dominant emotion, written reflection) instead of timetable slots.
 */
import { useMemo } from "react";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import {
  calendarColor,
  calendarBandLabel,
  weekDates,
  formatDateLong,
  dayOfWeekShort,
  addDays,
  toISO,
} from "@/lib/calendar";

export default function WeekView({ focusDate, onChangeFocus, days, selectedDate, onSelect }) {
  const week = useMemo(() => weekDates(focusDate), [focusDate]);
  const today = useMemo(() => toISO(new Date()), []);
  const byIso = useMemo(() => new Map((days || []).map((d) => [d.date, d])), [days]);

  const start = week[0].date;
  const end = week[6].date;
  const rangeLabel =
    start.getMonth() === end.getMonth()
      ? `${start.getDate()} – ${end.getDate()} de ${formatDateLong(week[0].iso).split(" de ")[1]}`
      : `${formatDateLong(week[0].iso)} – ${formatDateLong(week[6].iso)}`;

  const goto = (delta) => onChangeFocus(addDays(focusDate, delta * 7));

  // Week average
  const weekly = week
    .map((w) => byIso.get(w.iso)?.overall)
    .filter((v) => typeof v === "number");
  const avg = weekly.length ? weekly.reduce((a, b) => a + b, 0) / weekly.length : null;

  return (
    <div
      className="relative rounded-3xl p-5 sm:p-7 overflow-hidden backdrop-blur-md border"
      style={{
        background:
          "linear-gradient(155deg, rgba(255, 251, 240, 0.78) 0%, rgba(244, 236, 215, 0.45) 100%)",
        borderColor: "rgba(218, 207, 186, 0.5)",
        boxShadow: "0 18px 50px -28px rgba(83,109,83,0.22), 0 1px 0 rgba(255,255,255,0.65) inset",
      }}
      data-testid="calendar-week-view"
    >
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="tk-label">Semana</p>
          <h3 className="font-outfit text-xl text-ink-900 mt-0.5" data-testid="week-title">
            {rangeLabel}
          </h3>
          {avg != null && (
            <p className="font-dmsans text-xs text-ink-600 mt-1 flex items-center gap-1.5">
              <Sparkles size={12} className="text-sage-600" />
              Promedio de la semana:&nbsp;
              <span className="text-ink-900 font-medium">{avg.toFixed(1)}</span>
              &nbsp;· {calendarBandLabel(avg)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <NavButton onClick={() => goto(-1)} testId="week-prev"><ChevronLeft size={16} /></NavButton>
          <NavButton onClick={() => goto(1)} testId="week-next"><ChevronRight size={16} /></NavButton>
        </div>
      </div>

      <div className="space-y-2">
        {week.map((w) => {
          const data = byIso.get(w.iso);
          const overall = data?.overall ?? null;
          const isToday = w.iso === today;
          const isSelected = selectedDate === w.iso;
          const color = calendarColor(overall);

          return (
            <button
              key={w.iso}
              type="button"
              onClick={() => onSelect(w.iso)}
              className="w-full flex items-stretch gap-3 rounded-2xl p-3 sm:p-4 text-left transition-all duration-200 hover:translate-x-[2px] focus:outline-none"
              style={{
                background: isSelected
                  ? "rgba(222, 232, 221, 0.55)"
                  : "rgba(255, 251, 240, 0.55)",
                border: isSelected
                  ? "1px solid #2A9D8F"
                  : "1px solid rgba(218, 207, 186, 0.5)",
                boxShadow: isSelected
                  ? "0 8px 20px -10px rgba(42,157,143,0.35)"
                  : "0 4px 14px -10px rgba(83,109,83,0.15)",
              }}
              data-testid={`week-row-${w.iso}`}
            >
              {/* Color rail */}
              <span
                aria-hidden="true"
                className="rounded-full"
                style={{
                  width: 6,
                  minHeight: 56,
                  background:
                    overall != null
                      ? `linear-gradient(180deg, ${color} 0%, ${color} 100%)`
                      : "rgba(196, 178, 144, 0.25)",
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <div>
                    <p className="font-dmsans text-[10px] tracking-[0.16em] uppercase text-ink-600">
                      {dayOfWeekShort(w.iso)}{isToday && " · hoy"}
                    </p>
                    <p className="font-outfit text-base text-ink-900 mt-0.5">
                      {formatDateLong(w.iso)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-outfit text-2xl text-ink-900 leading-none">
                      {overall != null ? overall.toFixed(1) : "—"}
                    </p>
                    <p className="font-dmsans text-[10px] text-ink-600 mt-1">
                      {calendarBandLabel(overall)}
                    </p>
                  </div>
                </div>
                <p className="font-dmsans text-xs text-ink-600 mt-2 line-clamp-1">
                  {overall == null
                    ? "Sin registro este día. Tocá para agregar lo que recordés."
                    : data?.has_reflection
                    ? "Reflexión escrita disponible"
                    : "Día registrado"}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NavButton({ onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-9 w-9 rounded-full flex items-center justify-center text-ink-600 hover:text-ink-900 transition border backdrop-blur-md"
      style={{
        background: "rgba(255, 251, 240, 0.7)",
        borderColor: "rgba(218, 207, 186, 0.55)",
      }}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
